<template>
    <div class="deletion-guide-tab-container" :aria-busy="loading">
        <p class="sr-only" role="status">{{ guideStatus }}</p>
        <SharedSpinner v-if="loading" label="Loading deletion guide..." />
        <template v-else>
            <DeletionGuideAlert :has-exposure="Boolean(exposure)" />

            <!-- Deletion Guide Content -->
            <div v-if="deletionGuide" v-html="deletionGuide" class="deletion-guide-content" />
            <NoDeletionGuide v-else />
        </template>
    </div>
</template>

<script setup lang="ts">
import NoDeletionGuide from '~/components/main/person/companies/CompanyView/CompanyTabs/DeletionGuideTab/NoDeletionGuide.vue';
import { companiesService } from '~/services/companies-service';
import { useExposureSidebarStore } from '~/stores/components/modals/exposure-sidebar';
import DeletionGuideAlert from '../CompanySideBar/DeletionGuideAlert.vue';

const exposureStore = useExposureSidebarStore();
const { company, exposure } = storeToRefs(exposureStore);

const deletionGuide = ref<string | null>(null);
const loading = ref(false);
const loadFailed = ref(false);
const lastFetchedCompanyId = ref<number | null>(null);

const guideStatus = computed((): string => {
    if (loading.value) return 'Loading deletion guide';
    // "None available" and "the request failed" are different answers, and nothing else on
    // this tab reports the second one.
    if (loadFailed.value) return 'Deletion guide could not be loaded';
    return deletionGuide.value ? 'Deletion guide loaded' : 'No deletion guide available';
});

const loadDeletionGuide = async () => {
    if (!company.value?.id) {
        deletionGuide.value = null;
        loading.value = false;
        return;
    }

    // Only fetch if we haven't already fetched for this company
    if (lastFetchedCompanyId.value === company.value.id) {
        return;
    }

    loadFailed.value = false;

    try {
        loading.value = true;
        lastFetchedCompanyId.value = company.value.id;
        deletionGuide.value = await companiesService.getDeletionGuide(company.value.id);
    } catch (error) {
        console.error('Failed to fetch deletion guide:', error);
        loadFailed.value = true;
        deletionGuide.value = null;
    } finally {
        loading.value = false;
    }
};

watch(() => company.value?.id, (newId) => {
    if (newId && newId !== lastFetchedCompanyId.value) {
        loadDeletionGuide();
    }
}, { immediate: true });
</script>

<style lang="scss" scoped>
.deletion-guide-tab-container {
    @apply flex flex-col;
}

.deletion-guide-content {
    @apply bg-gray-50/50 gap-4 flex flex-col text-zinc-950 px-6 pt-6;

    :deep(p) {
        @apply mb-4 text-zinc-950 leading-relaxed;
    }

    :deep(h3) {
        @apply text-zinc-950;
    }
    
    :deep(strong) {
        @apply font-semibold text-zinc-950;
    }
    
    :deep(ul) {
        @apply mb-4 pl-6 list-disc;
    }
    :deep(ol) {
        @apply mb-4 pl-6 list-decimal;
    }
    
    :deep(li) {
        @apply mb-2 text-zinc-950;
    }
}
</style>
